import { datadogLogs } from '@datadog/browser-logs';
import io, { Socket } from 'socket.io-client';
import { JsonValue, Unsubscribable, Observer } from 'type-fest';

import { callNextObservers } from '@frontend/utils';

import { SOCKETS_URL } from 'constants/all';

import authService from 'services/authService';

import { keysToCamelCaseDeep } from 'utils/stringUtils';
import { getToken } from 'utils/token';

const DEFAULT_WEBSOCKET_MESSAGE_EVENT = 'message' as const;

class SocketClientSingleton {
  // promise responsible for making sure there is one socket create request at a time
  private socketInitializationPromise: Promise<void> | null = null;
  private _socket: Socket | null = null;
  private topicSubscriptions = new Map<string, Set<Observer<JsonValue>>>();
  private debounceTimers: Map<string, ReturnType<typeof setTimeout>> =
    new Map();
  private pendingSubscriptionTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private static instance: SocketClientSingleton | null = null;
  private logPrefix = '[webSocketService]';

  constructor() {
    if (!SocketClientSingleton.instance) {
      // implement a singleton
      SocketClientSingleton.instance = this;
    }

    return SocketClientSingleton.instance;
  }

  private async _performSocketInitialization(): Promise<void> {
    try {
      const { socketToken } = await authService.websocket.init();
      this._socket = io(SOCKETS_URL, {
        query: {
          socket_token: socketToken,
        },
        reconnection: false,
        withCredentials: true,
      });
      this.manageSocket();
    } catch (error) {
      console.error(
        `${this.logPrefix} Received error when attempting to initialize websocket.`,
        error,
      );
      datadogLogs.logger.error(
        `${this.logPrefix} Received error when attempting to initialize websocket.`,
        {},
        error as Error,
      );
    }
  }

  /**
   * Establishes a websocket connection to the socket server by:
   * 1. Getting the socket token from the Paperless Parts API
   * 2. Using that token to register a socket with the backend
   */
  public async initializeSocket(): Promise<void> {
    const token = getToken();

    if (!SOCKETS_URL) {
      throw new Error('SOCKETS_URL is not defined in environment variables');
    }

    // make sure we have a token and access to the mutex (ie. we are the only promise creating a new socket)
    if (token) {
      if (!this.socketInitializationPromise) {
        this.socketInitializationPromise = this._performSocketInitialization();
      }
      await this.socketInitializationPromise;
      this.socketInitializationPromise = null;
    } else {
      console.warn(
        `${this.logPrefix} No access token found when attempting to initialize websocket`,
      );
      datadogLogs.logger.warn(
        `${this.logPrefix} No access token found when attempting to initialize websocket`,
      );
    }
  }

  /**
   * Handles returning or creating a socket connection.
   * @private
   */
  private async socket(): Promise<Socket | null> {
    if (!this._socket) {
      await this.initializeSocket();
    }
    return this._socket;
  }

  /**
   * If a socket exists, registers a new topic by sending a subscription message to the socket server.
   * @param topic - The topic the socket server will listen to.
   * @private
   */
  private async subscribeTopicToServer(
    topic: string,
    attemptCount = 0,
  ): Promise<void> {
    // if there is an existing debounce timer for unsubscribe, clear it
    if (this.debounceTimers.has(topic)) {
      clearTimeout(this.debounceTimers.get(topic));
      this.debounceTimers.delete(topic);
    }

    const socket = await this.socket();
    if (socket) {
      if (socket.connected) {
        socket.emit('subscribe', JSON.stringify({ topic }));
        if (attemptCount > 0) {
          console.warn(
            `${this.logPrefix} Succesfully subscribed to topic: ${topic} after ${attemptCount} attempts`,
          );
          datadogLogs.logger.warn(
            `${this.logPrefix} Successfully subscribed to topic: ${topic} after ${attemptCount} attempts`,
            {
              topic,
              attemptCount,
            },
          );
        }
      } else {
        this.setupSubscriptionRetry(topic, attemptCount + 1);
      }
    }
  }

  /**
   * Subscribe a callback to listen for a topic.
   * @param topic - The topic to subscribe to on the websocket.
   * @param callback - The callback when a message is published to the topic.
   * @param unsubscribeDebounceTime - optional time to debounce unsubscribing
   */
  public subscribe(
    topic: string,
    callback: (param: JsonValue) => void,
    unsubscribeDebounceTime = 0,
  ): Unsubscribable {
    let topicCallbacks = this.topicSubscriptions.get(topic);
    if (!topicCallbacks) {
      topicCallbacks = new Set();
      this.topicSubscriptions.set(topic, topicCallbacks);
      this.subscribeTopicToServer(topic, 0);
    }

    const observer: Observer<JsonValue> = {
      next: callback,
      error: (err: unknown) => {
        console.error(`${this.logPrefix} error in observer!`, err);
        datadogLogs.logger.error(
          `${this.logPrefix} error in observer!`,
          {},
          err as Error,
        );
      },
      complete: function () {
        if (topicCallbacks) {
          topicCallbacks.delete(this);
        }
      },
    };
    topicCallbacks.add(observer);

    return {
      unsubscribe: () => {
        if (topicCallbacks) {
          topicCallbacks.delete(observer);
          if (topicCallbacks.size === 0) {
            this.topicSubscriptions.delete(topic);
            this.unsubscribeTopicFromServer(topic, unsubscribeDebounceTime);
          }
        }
      },
    };
  }

  private setupSubscriptionRetry(topic: string, attemptCount = 1): void {
    const MAX_ATTEMPT_COUNT = 3;
    if (attemptCount > MAX_ATTEMPT_COUNT) {
      console.warn(
        `${this.logPrefix} Max retry count reached for topic: ${topic}`,
      );
      datadogLogs.logger.warn(
        `${this.logPrefix} Max retry count reached for topic: ${topic}`,
        {
          topic,
          attemptCount,
        },
      );
      return;
    }

    // exponential backoff
    const delay = 1000 * Math.pow(2, attemptCount - 1);
    if (!this.pendingSubscriptionTimers.has(topic)) {
      const timer = setTimeout(() => {
        if (this._socket?.connected) {
          console.warn(
            `${this.logPrefix} Retrying subscription for topic: ${topic}`,
          );
          datadogLogs.logger.warn(
            `${this.logPrefix} Retrying subscription for topic: ${topic}`,
            {
              topic,
              attemptCount,
            },
          );
          this.subscribeTopicToServer(topic, attemptCount + 1);
        }
        this.pendingSubscriptionTimers.delete(topic);
      }, delay);
      this.pendingSubscriptionTimers.set(topic, timer);
    }
  }

  /**
   * Handles shutting down a socket and resetting all the observers.
   * Closing the socket will clear all topics on the server because the queues will be destroyed.
   */
  public shutDown(): void {
    if (this._socket) {
      this._socket.close();
      this._socket = null;
    }
    this.topicSubscriptions.clear();
  }

  /**
   * Handles invoking the callbacks for messages from the websocket. We will call every callback that was subscribed to the topic.
   * @param rawMessage - An object where the key is the name of the topic and the value is a stringified json message
   * @private
   */
  private onMessage(
    rawMessage: Record<string, string>,
    callback: (arg: any) => void,
  ): void {
    try {
      // a message may include one or more topic responses
      for (const topic of Object.keys(rawMessage)) {
        let message;
        if (typeof rawMessage[topic] === 'string') {
          message = keysToCamelCaseDeep(JSON.parse(rawMessage[topic])); // TODO: LOOK INTO THIS FORMAT
        } else {
          message = keysToCamelCaseDeep(rawMessage[topic]);
        }
        const topicCallbacks = this.topicSubscriptions.get(topic);
        if (topicCallbacks) {
          callNextObservers(topicCallbacks, message);
        }

        if (!message['autoack']) {
          callback('ack');
        }
      }
    } catch (error) {
      console.error(
        `${this.logPrefix} Received an error when attempting to handle message from the websocket`,
        error,
      );
      datadogLogs.logger.error(
        `${this.logPrefix} Received an error when attempting to handle message from the websocket`,
        {},
        error as Error,
      );
    }
  }

  /**
   * Handles socket.io disconnect events by following the recommended approach from their docs.
   * https://socket.io/docs/v4/client-api/#event-disconnect
   * @param reason
   * @private
   */
  private onDisconnect(reason: string): void {
    console.warn(
      `${this.logPrefix} Websocket connection disconnected for reason: ${reason}`,
    );
    datadogLogs.logger.warn(
      `${this.logPrefix} Websocket connection disconnected for reason: ${reason}`,
      {
        reason,
      },
    );
    if (reason === 'io server disconnect') {
      console.warn(
        `${this.logPrefix} Attempting to re-establish websocket connection`,
      );
      datadogLogs.logger.warn(
        `${this.logPrefix} Attempting to re-establish websocket connection`,
      );
      // the disconnection was initiated by the server, you need to reconnect manually
      this._socket?.connect();
    }
  }

  /**
   * Reconnect socket connection on socket.io error
   * @param error
   * @private
   */
  private onError(error: Error): void {
    console.warn(
      `${this.logPrefix} Websocket connection received an error`,
      error,
    );
    datadogLogs.logger.error(
      `${this.logPrefix} Websocket connection received an error`,
      {},
      error,
    );
    this._socket = null;
    this.initializeSocket();
  }

  /**
   * Responsible for establishing the events needed for the socket to function.
   * @private
   */
  private manageSocket(): void {
    if (this._socket) {
      // 1. Establish connection for Paperless Parts events flowing from the backend
      this._socket.on(
        DEFAULT_WEBSOCKET_MESSAGE_EVENT,
        (data: Record<string, string>, callback: (arg: any) => void) =>
          this.onMessage(data, callback),
      );
      // 2. Handle socket.io error events
      this._socket.on('connect_error', (error: Error) => this.onError(error));
      this._socket.io.on('error', (error: Error) => this.onError(error));
      // 3. Handle socket.io disconnect event
      this._socket.on('disconnect', (reason: string) =>
        this.onDisconnect(reason),
      );
      // 4. Handle socket.io connect event
      this._socket.on('connect', () => {
        // Sync current socket subscriptions with the backend
        this.registerExistingTopics();
      });
    } else {
      console.warn(
        `${this.logPrefix} Attempted to register event listeners for SocketClient when websocket connection does not exist.`,
      );
      datadogLogs.logger.warn(
        `${this.logPrefix} Attempted to register event listeners for SocketClient when websocket connection does not exist.`,
      );
    }
  }

  /**
   * Registers all previously subscribed topics through the socket.
   * This is useful when re-establishing a connection and you need to re-sync the states.
   * @private
   */
  private registerExistingTopics(): void {
    this.topicSubscriptions.forEach((callbacks, topic) => {
      if (callbacks.size) {
        this.subscribeTopicToServer(topic);
      }
    });
  }

  /**
   * Unsubscribes from a topic on the server. Makes sure there are no valid listeners for that topic.
   * Debounces this call against new subscription calls of the same topic to prevent
   * flickering of subscribe/unsubscribe
   * @param topic - The topic the socket server will listen to.
   * @param debounceTime - time to debounce the unsubscribe call
   * @private
   */
  private unsubscribeTopicFromServer(topic: string, debounceTime = 0): void {
    // Debounce the unsubscribe call
    if (this.debounceTimers.has(topic)) {
      clearTimeout(this.debounceTimers.get(topic));
    }

    // Clear any pending subscription timer for this topic
    if (this.pendingSubscriptionTimers.has(topic)) {
      clearTimeout(this.pendingSubscriptionTimers.get(topic));
      this.pendingSubscriptionTimers.delete(topic);
    }

    const timer = setTimeout(async () => {
      const socket = await this.socket();
      if (socket) {
        const topicSubscription = this.topicSubscriptions.get(topic);
        if (topicSubscription && topicSubscription.size !== 0) {
          console.warn(
            `${this.logPrefix} Attempted to unsubscribe from a websocket topic with active listeners.`,
          );
          datadogLogs.logger.warn(
            `${this.logPrefix} Attempted to unsubscribe from a websocket topic with active listeners.`,
            {
              topic,
            },
          );
        } else {
          socket.emit('unsubscribe', JSON.stringify({ topic }));
        }
      } else {
        console.warn(
          `${this.logPrefix} Attempted to unsubscribe to websocket topic=${topic} when no websocket connection has been established.`,
        );
        datadogLogs.logger.warn(
          `${this.logPrefix} Attempted to unsubscribe to websocket topic=${topic} when no websocket connection has been established.`,
          {
            topic,
          },
        );
      }
    }, debounceTime);

    this.debounceTimers.set(topic, timer);
  }
}

const socketClientSingleton = new SocketClientSingleton();

export default socketClientSingleton;
